import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from 'app/services/apiService';

const initialState = {
  complianceData: {
    compliance: {
      complianceRates: [],
      endDate: '',
      policyName: '',
      startDate: '',
      status: '',
    },
    complianceLoading: false,
    complianceError: {},
  },
  trendsData: {
    trends: {
      dataClassLevels: [],
      endDate: '',
      policyName: '',
      startDate: '',
      status: '',
    },
    trendsLoading: false,
    trendsError: {},
  },
  objectsAndDeviations: {
    summed: {
      totNrOfObjects: [],
      totNrOfDeviations: [],
      endDate: '',
      policyName: '',
      startDate: '',
      status: '',
    },
    summedLoading: false,
    summedError: {},
  },
  deviationsPerDataSource: {
    data: { status: '', hint: '', informationTypes: {}, datasources: [] },
    loading: false,
    error: {},
  },
  selectedInformationTypes: [{ name: 'All', id: 'all' }],
  selectedDataSources: [{ name: 'All', id: 'all' }],
  selectedDataSource: [{ name: 'Data Sources', id: 'dataSources' }],
  selectedInformationClasses: [{ name: 'Information Classes', id: 'informationClasses' }],
  selectedInformationType: [{ name: 'Information Types', id: 'informationTypes' }],
  selectedRestrictions: [{ name: 'Violations / Restrictions', id: 'restrictions' }],
  deviationsPerInformationTypes: {
    data: {
      status: '',
      hint: '',
      informationTypes: {},
      dataClasses: [],
    },
    loading: false,
    error: {},
  },
  estimatedDeviationsReportSize: {},
  exportDeviationsReportRequest: {},
};

export const getComplianceData = createAsyncThunk(
  'getComplianceData',
  async ({ policyTypeId, query }, { rejectWithValue }) => {
    try {
      const { data } = await apiService.fetchComplianceData(policyTypeId, query);
      return data;
    } catch (error) {
      rejectWithValue(error.message);
      return error;
    }
  }
);

export const getTrendsData = createAsyncThunk(
  'getTrendsData',
  async ({ policyTypeId, query }, { rejectWithValue }) => {
    try {
      const { data } = await apiService.fetchTrendsData(policyTypeId, query);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getObjectsAndDeviations = createAsyncThunk(
  'getObjectsAndDeviations',
  async ({ policyTypeId, query }, { rejectWithValue }) => {
    try {
      const { data } = await apiService.fetchObjectsAndDeviations(policyTypeId, query);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getDeviationsPerDataSource = createAsyncThunk(
  'getDeviationsPerDataSource',
  async ({ policyTypeId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await apiService.fetchDeviationsPerDataSource(policyTypeId, payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getDeviationsPerInfoType = createAsyncThunk(
  'getDeviationsPerInfoType',
  async ({ policyTypeId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await apiService.fetchDeviationsPerInfoType(policyTypeId, payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getEstimatedDeviationsReportSize = createAsyncThunk(
  'getEstimatedDeviationsReportSize',
  async ({ payload }, { rejectWithValue }) => {
    try {
      const { data } = await apiService.fetchEstimatedDeviationsReportSize(payload.data);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getExportDeviationsReportRequest = createAsyncThunk(
  'getExportDeviationsReportRequest',
  async ({ payload }, { rejectWithValue }) => {
    try {
      const { data } = await apiService.fetchExportDeviationsReportRequest(payload.data);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const complianceSlice = createSlice({
  name: 'compliance',
  initialState,
  reducers: {
    resetComplianceData(state) {
      state.complianceData = initialState.complianceData;
    },

    resetTrendsData(state) {
      state.trendsData = initialState.trendsData;
    },

    resetObjectsAndDeviations(state) {
      state.objectsAndDeviations = initialState.objectsAndDeviations;
    },

    setInformationTypes(state, { payload }) {
      state.selectedInformationTypes = payload;
    },
    setDataSources(state, { payload }) {
      state.selectedDataSources = payload;
    },
  },

  extraReducers(builder) {
    builder.addCase(getComplianceData.fulfilled, (state, { payload }) => {
      state.complianceData.compliance = payload;
      state.complianceData.complianceLoading = false;
    });
    builder.addCase(getComplianceData.pending, state => {
      state.complianceData.complianceLoading = true;
    });
    builder.addCase(getComplianceData.rejected, (state, { payload }) => {
      state.complianceData.complianceLoading = false;
      state.complianceData.complianceError = payload.response.data;
    });

    builder.addCase(getTrendsData.fulfilled, (state, { payload }) => {
      state.trendsData.trends = payload;
      state.trendsData.trendsLoading = false;
    });
    builder.addCase(getTrendsData.pending, state => {
      state.trendsData.trendsLoading = true;
    });
    builder.addCase(getTrendsData.rejected, (state, { payload }) => {
      state.trendsData.trendsLoading = false;
      state.trendsData.trendsError = payload.response.data;
    });

    builder.addCase(getDeviationsPerDataSource.fulfilled, (state, { payload }) => {
      state.deviationsPerDataSource.data = payload;
      state.deviationsPerDataSource.loading = false;
    });
    builder.addCase(getDeviationsPerDataSource.pending, state => {
      state.deviationsPerDataSource.loading = true;
    });
    builder.addCase(getDeviationsPerDataSource.rejected, (state, { payload }) => {
      state.deviationsPerDataSource.loading = false;
      state.deviationsPerDataSource.error = payload.response.data;
    });

    builder.addCase(getDeviationsPerInfoType.fulfilled, (state, { payload }) => {
      state.deviationsPerInformationTypes.data = payload;
      state.deviationsPerInformationTypes.loading = false;
    });
    builder.addCase(getDeviationsPerInfoType.pending, state => {
      state.deviationsPerInformationTypes.loading = true;
    });
    builder.addCase(getDeviationsPerInfoType.rejected, (state, { payload }) => {
      state.deviationsPerInformationTypes.loading = false;
      state.deviationsPerInformationTypes.error = payload.response.data;
    });

    builder.addCase(getObjectsAndDeviations.fulfilled, (state, { payload }) => {
      state.objectsAndDeviations.summed = payload;
      state.objectsAndDeviations.summedLoading = false;
    });
    builder.addCase(getObjectsAndDeviations.pending, state => {
      state.objectsAndDeviations.summedLoading = true;
    });
    builder.addCase(getObjectsAndDeviations.rejected, (state, { payload }) => {
      state.objectsAndDeviations.summedLoading = false;
      state.objectsAndDeviations.summedError = payload.response.data;
    });

    builder.addCase(getEstimatedDeviationsReportSize.fulfilled, (state, { payload }) => {
      state.estimatedDeviationsReportSize = payload;
      state.estimatedDeviationsReportSize.loading = false;
    });

    builder.addCase(getExportDeviationsReportRequest.fulfilled, (state, { payload }) => {
      state.exportDeviationsReportRequest = payload;
      state.exportDeviationsReportRequest.loading = false;
    });
  },
});

export const { resetComplianceData, resetTrendsData, setInformationTypes, setDataSources } =
  complianceSlice.actions;
export default complianceSlice.reducer;
